import { Price, topliApi } from '@/shared/api/topli/TopliApi'
import { CreateUnitFormState, CreateUnitResponse } from '../model/types'
import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  unitListsByBuildingIdQueryKey,
  unitListsByPageQueryKey,
} from '@/entities/Unit/api/unit-api'
import { errorHandler } from '@/shared/lib/errorHandler'
import { addPricesApi } from '@/features/Price/CreatePriceFormField'
import { currentBuildingByIdQueryKey } from '@/entities/Building'
import { ModelTypes } from '@/shared/api/topli/TopliApi'
import { addTranslation } from '@/entities/ContentTranslation'

export const createUnit = async (state: CreateUnitFormState): Promise<CreateUnitResponse> => {
  const { prices, translations, level, building_id, building_level, ...restState } = state
  const isLastFloor = building_level === level
  const pricesInCents = prices
    .filter(item => item.price > 0)
    .map(price => ({ ...price, price: Number(price.price * 100) }))

  const response = await topliApi.unit.create(
    { item_id: null },
    {
      name: translations.tr.name,
      description: translations.tr.description,
      last_floor: isLastFloor,
      level,
      building_id,
      unit_positions: {},
      agents_comments: {},
      clients_likes: {},
      ...restState,
    },
  )
  const modifiedResponse = { ...response, prices: pricesInCents }
  return modifiedResponse
}

export const useCreateUnitApi = (): UseMutationResult<
  CreateUnitResponse,
  Error,
  CreateUnitFormState
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: state => createUnit(state),
    onSuccess: async (res, state) => {
      try {
        await addTranslation(
          {
            translations: state.translations,
            modelType: ModelTypes.Unit,
          },
          res.id,
        )

        if (res.prices.length > 0) {
          await addPricesApi([...res.prices] as Price[], res.id)
        }
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [unitListsByPageQueryKey],
            refetchType: 'active',
          }),
          queryClient.invalidateQueries({
            queryKey: [currentBuildingByIdQueryKey],
            refetchType: 'active',
          }),
          queryClient.invalidateQueries({
            queryKey: [unitListsByBuildingIdQueryKey],
            refetchType: 'active',
          }),
        ])
      } catch (error) {
        errorHandler(error)
      }
    },
  })
}
